import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ShieldCheck } from 'lucide-react';

const KEY = 'nuyed_cookie_consent';
const VERSION = 2;
export const COOKIE_CATEGORIES = [
  ['essential', 'Strictly necessary', 'Login session, security (2FA, CSRF), cart, theme and cookie preferences. Always on.', true],
  ['preferences', 'Preferences', 'Remembers UI choices such as sidebar state, filters and language.', false],
  ['analytics', 'Analytics', 'Anonymous usage statistics that help us improve NUYED. No ad tracking.', false],
  ['marketing', 'Embedded media', 'Allows YouTube, Twitch, Kick, X, TikTok and other embeds that may set their own cookies.', false],
];

export function getConsent() {
  try { const c = JSON.parse(localStorage.getItem(KEY)); return c && c.v === VERSION ? c : null; } catch { return null; }
}
function saveConsent(choices) {
  const c = { v: VERSION, ts: Date.now(), essential: true, ...choices };
  try { localStorage.setItem(KEY, JSON.stringify(c)); } catch {}
  window.dispatchEvent(new CustomEvent('nuyed:consent', { detail: c }));
  return c;
}
export function openCookieSettings() { window.dispatchEvent(new CustomEvent('nuyed:cookie-settings')); }

export default function CookieConsent() {
  const [open, setOpen] = useState(() => !getConsent());
  const [custom, setCustom] = useState(false);
  const [choices, setChoices] = useState(() => { const c = getConsent(); return { preferences: c?.preferences ?? true, analytics: c?.analytics ?? false, marketing: c?.marketing ?? true }; });

  useEffect(() => {
    const h = () => { setCustom(true); setOpen(true); };
    window.addEventListener('nuyed:cookie-settings', h);
    return () => window.removeEventListener('nuyed:cookie-settings', h);
  }, []);
  useEffect(() => { document.body.classList.toggle('gx-cookie-open', open); return () => document.body.classList.remove('gx-cookie-open'); }, [open]);
  if (!open) return null;

  const done = (c) => { saveConsent(c); setOpen(false); setCustom(false); };
  const acceptAll = () => done({ preferences: true, analytics: true, marketing: true });
  const essentialOnly = () => done({ preferences: false, analytics: false, marketing: false });

  return (
    <aside className="gx-cookie" role="dialog" aria-live="polite" aria-label="Cookie preferences" data-testid="cookie-banner">
      {getConsent() && <button className="gx-cookie-close" onClick={() => setOpen(false)} aria-label="Close" data-testid="cookie-close"><X className="w-4 h-4" /></button>}
      <div className="gx-cookie-head">
        <span className="gx-cookie-ic"><Cookie /></span>
        <div><p className="gx-cookie-title">Your privacy, your call</p><p className="gx-cookie-sub">GDPR · ePrivacy · CCPA compliant</p></div>
      </div>
      <p className="gx-cookie-text">
        We use strictly necessary cookies to keep you signed in and secure your NUY wallet. With your consent we also use preference, analytics and embedded-media cookies.
        Read our <Link to="/cookies">Cookie Policy</Link> and <Link to="/privacy">Privacy Policy</Link>.
      </p>
      {custom && (
        <div className="gx-cookie-opts" data-testid="cookie-options">
          {COOKIE_CATEGORIES.map(([key, name, desc, locked]) => {
            const on = locked || choices[key];
            return (
              <div key={key} className="gx-cookie-opt" data-testid={`cookie-opt-${key}`}>
                <div className="min-w-0"><b>{name}</b><small>{desc}</small></div>
                <button type="button" role="switch" aria-checked={on} disabled={locked} onClick={() => !locked && setChoices({ ...choices, [key]: !choices[key] })} className={`gx-switch ${on ? 'is-on' : ''} ${locked ? 'is-locked' : ''}`} data-testid={`cookie-switch-${key}`}><span /></button>
              </div>
            );
          })}
        </div>
      )}
      <div className="gx-cookie-actions">
        <button type="button" className="gx-btn-primary" onClick={acceptAll} data-testid="cookie-accept-all">Accept all</button>
        {custom ? (
          <button type="button" className="gx-btn-ghost" onClick={() => done(choices)} data-testid="cookie-save"><ShieldCheck className="w-3.5 h-3.5" /> Save preferences</button>
        ) : (
          <button type="button" className="gx-btn-ghost" onClick={essentialOnly} data-testid="cookie-essential">Essential only</button>
        )}
        {!custom && <button type="button" className="gx-cookie-link" onClick={() => setCustom(true)} data-testid="cookie-customize">Customize preferences</button>}
      </div>
    </aside>
  );
}
